import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, MessageCircle, Check, Sparkles, Minus, Plus,
  Upload, Palette, FileText, ChevronDown, ChevronUp, Truck, Clock, X, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import usePageTitle from "@/hooks/use-page-title";
import { useCart, shopClient } from "@/context/CartContext";
import { getDiscountInfo } from "@/lib/discountUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ACCENTS = {
  "Business Cards": "from-orange-500 to-red-500",
  "Business Stationery": "from-blue-600 to-indigo-700",
  "Marketing Materials": "from-emerald-500 to-teal-600",
  "Label & Packaging": "from-amber-500 to-orange-600",
  "Indoor & Outdoor": "from-purple-600 to-pink-600",
  "Personalised & Gifting": "from-rose-500 to-fuchsia-600",
};

export default function ProductDetail() {
  usePageTitle("Product");
  const { id } = useParams();
  const nav = useNavigate();
  const { addToCart } = useCart();
  const [p, setP] = useState(null);
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [adding, setAdding] = useState(false);
  const [price, setPrice] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);
  const [activeImage, setActiveImage] = useState(0);
  // Design choice state — "upload_now" | "upload_later" | "design_for_me"
  const [designChoice, setDesignChoice] = useState("upload_now");
  const [designFiles, setDesignFiles] = useState([]); // [{media_id, url, filename, size}]
  const [designNotes, setDesignNotes] = useState("");
  const [uploadingDesign, setUploadingDesign] = useState(false);

  useEffect(() => {
    fetch(`${API}/catalog/${id}`).then((r) => r.json()).then((data) => {
      if (data && data.id) {
        setP(data);
        // Set defaults (skip legacy `designer` option — superseded by design_choice UI below)
        const def = {};
        (data.options || []).filter((o) => o.key !== "designer").forEach((o) => {
          const d = (o.values || []).find((v) => v.is_default) || (o.values || [])[0];
          if (d) def[o.key] = d.value;
        });
        setSelected(def);
        // Start at the first tier's quantity if defined, otherwise min_quantity
        const firstTierQty = (data.quantity_tiers && data.quantity_tiers[0]?.quantity) || data.min_quantity || 1;
        setQty(firstTierQty);
      } else nav("/products");
    });
  }, [id, nav]);

  useEffect(() => {
    if (!p) return;
    fetch(`${API}/catalog?active_only=true`).then((r) => r.json()).then((rows) => {
      setRelated(rows.filter((r) => r.group === p.group && r.id !== p.id).slice(0, 4));
    });
  }, [p]);

  // Recompute price on qty/options/design-choice change (with abort for race safety)
  useEffect(() => {
    if (!p) return;
    const ctrl = new AbortController();
    fetch(`${API}/catalog/price`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: p.id, quantity: qty, options: selected, design_choice: designChoice }),
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((data) => setPrice(data))
      .catch(() => {});
    return () => ctrl.abort();
  }, [p, qty, designChoice, JSON.stringify(selected)]);

  if (!p) return <div className="max-w-6xl mx-auto px-4 py-24 text-center text-zinc-500">Loading…</div>;

  const accent = GROUP_ACCENTS[p.group] || "from-zinc-500 to-zinc-700";
  const canBuy = p.is_ecommerce !== false && p.active !== false;
  const stepQty = (d) => {
    const step = p.quantity_step || 1;
    const min = p.min_quantity || 1;
    const max = p.max_quantity && p.max_quantity > 0 ? p.max_quantity : Infinity;
    const next = Math.max(min, Math.min(max, qty + d * step));
    setQty(next);
  };

  const handleAdd = async () => {
    // Guard: if the customer picked "Upload Now" they must actually upload at least one file
    if (designChoice === "upload_now" && designFiles.length === 0) {
      toast.error("Please upload your design file, or choose 'Upload later' / 'Design for me'");
      return;
    }
    setAdding(true);
    try {
      await addToCart(p.id, qty, selected, {
        design_choice: designChoice,
        design_files: designFiles,
        design_notes: designNotes,
      });
      toast.success(`${qty} × ${p.name} added to cart`);
    } catch (e) { toast.error("Could not add to cart"); }
    finally { setAdding(false); }
  };

  const handleDesignFileUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    // 25 MB soft cap on frontend (backend enforces its own limit too)
    if (file.size > 50 * 1024 * 1024) { toast.error("File is too large (max 50 MB). Please compress or share via WhatsApp."); return; }
    setUploadingDesign(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await shopClient.post("/shop/cart/design-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDesignFiles((prev) => [...prev, {
        media_id: data.media_id, url: data.url, filename: data.filename, size: data.size, uploaded_at: data.uploaded_at,
      }]);
      toast.success(`Uploaded ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingDesign(false);
      ev.target.value = "";
    }
  };

  const removeDesignFile = (mid) => setDesignFiles((prev) => prev.filter((f) => f.media_id !== mid));

  const handleBuyNow = async () => {
    // Guard: same validation as handleAdd; abort navigation if it fails
    if (designChoice === "upload_now" && designFiles.length === 0) {
      toast.error("Please upload your design file, or choose 'Upload later' / 'Design for me'");
      return;
    }
    setAdding(true);
    try {
      await addToCart(p.id, qty, selected, {
        design_choice: designChoice,
        design_files: designFiles,
        design_notes: designNotes,
      });
      nav("/checkout");
    } catch (e) { toast.error("Could not add to cart"); }
    finally { setAdding(false); }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-6 text-xs text-zinc-500">
        <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/products?g=${encodeURIComponent(p.group)}`} className="hover:text-[#FF6B00]">{p.group}</Link>
        <span className="mx-2">/</span>
        <span className="text-zinc-800 font-semibold">{p.name}</span>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Image gallery */}
        <div className="lg:col-span-2 space-y-3">
          {(() => {
            const gallery = (p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []));
            const currentImg = gallery[activeImage] || p.image_url;
            return (
              <>
                <div className="rounded-2xl overflow-hidden border-2 border-zinc-200 aspect-square relative">
                  {currentImg ? (
                    <>
                      <img
                        src={currentImg}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                        data-testid="pdp-main-image"
                      />
                      <div className={`hidden w-full h-full bg-gradient-to-br ${accent} items-center justify-center p-8 absolute inset-0`}>
                        <div className="text-white font-display text-4xl md:text-5xl font-black text-center leading-tight">
                          {p.name}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${accent} flex items-center justify-center p-8 relative`}>
                      <div className="absolute top-4 left-4 text-[10px] uppercase tracking-widest text-white/80 font-bold">
                        {p.group} · {p.category}
                      </div>
                      <div className="text-white font-display text-4xl md:text-5xl font-black text-center leading-tight tracking-tight">
                        {p.name}
                      </div>
                    </div>
                  )}
                  {p.trending && (
                    <div className="absolute top-4 right-4 text-[10px] uppercase font-bold text-white bg-black/50 px-3 py-1 rounded-full backdrop-blur">Trending</div>
                  )}
                  {p.best_seller && !p.trending && (
                    <div className="absolute top-4 right-4 text-[10px] uppercase font-bold text-white bg-emerald-600 px-3 py-1 rounded-full">Best Seller</div>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div className="grid grid-cols-5 gap-2" data-testid="pdp-thumbnails">
                    {gallery.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeImage === i ? "border-[#FF6B00]" : "border-zinc-200 hover:border-zinc-400"}`}
                        data-testid={`thumb-${i}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
          {/* Feature bullets */}
          {(p.features || []).length > 0 && (
            <div className="p-4 border-2 border-zinc-100 rounded-2xl bg-zinc-50">
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Product Highlights</div>
              <ul className="space-y-1.5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-zinc-700">
                    <Check size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: Configurator */}
        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF6B00]">{p.category}</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight" data-testid="product-title">
            {p.name}
          </h1>
          {p.subtitle && <p className="mt-2 text-zinc-600 text-sm">{p.subtitle}</p>}

          <div className="mt-4 flex items-baseline gap-3 flex-wrap">
            <div className="text-xs uppercase text-zinc-500 font-bold tracking-wider">Starts from</div>
            <div className="font-mono text-[#FF6B00] font-black text-3xl" data-testid="product-starting-price">
              ₹{Math.round((p.quantity_tiers?.[0]?.unit_price ?? p.base_price) * (p.min_quantity || 1)).toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-zinc-500">/ {p.min_quantity || 1} {p.unit}</div>
            {(() => {
              const startUnit = p.quantity_tiers?.[0]?.unit_price ?? p.base_price;
              const d = getDiscountInfo(p, startUnit);
              if (!d.hasDiscount) return null;
              const mrpTotal = Math.round(d.mrp * (p.min_quantity || 1));
              return (
                <div className="inline-flex items-baseline gap-2" data-testid="pdp-discount">
                  <div className="font-mono text-zinc-400 line-through text-xl">₹{mrpTotal.toLocaleString("en-IN")}</div>
                  <div className="inline-flex items-center gap-1 bg-red-600 text-white text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm" data-testid="pdp-discount-badge">
                    {d.pct}% OFF
                  </div>
                </div>
              );
            })()}
          </div>

          {canBuy && (
            <div className="mt-5 p-5 border-2 border-zinc-200 rounded-2xl bg-white space-y-5" data-testid="configurator">
              <div className="font-bold text-lg tracking-tight">Customize & Estimate</div>

              {(p.options || []).filter((o) => o.key !== "designer").map((opt) => (
                <OptionRow
                  key={opt.key}
                  option={opt}
                  value={selected[opt.key]}
                  designPages={p.design_pages || 1}
                  onChange={(v) => setSelected((s) => ({ ...s, [opt.key]: v }))}
                />
              ))}

              {/* Quantity — customer picks from preset tiers only */}
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider font-bold text-zinc-600">Quantity</div>
                  <div className="text-[10px] text-zinc-500">Pick a quantity to see bulk savings</div>
                </div>
                {(() => {
                  const tiers = (p.quantity_tiers && p.quantity_tiers.length)
                    ? p.quantity_tiers
                    : [{ quantity: p.min_quantity || 1, unit_price: p.base_price || 0 }];
                  return (
                    <select
                      value={qty}
                      onChange={(e) => setQty(Number(e.target.value))}
                      className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-4 py-3 text-sm font-bold outline-none bg-white cursor-pointer"
                      data-testid="qty-select"
                    >
                      {tiers.map((t) => (
                        <option key={t.quantity} value={t.quantity} data-testid={`qty-opt-${t.quantity}`}>
                          {t.quantity} {p.unit || "pcs"} — ₹{Math.round(t.quantity * t.unit_price).toLocaleString("en-IN")} total (₹{t.unit_price}/{p.unit || "pc"})
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>

              {/* Quantity tier table */}
              {(p.quantity_tiers || []).length > 0 && (
                <div className="border border-zinc-200 rounded-xl overflow-hidden text-xs">
                  <div className="grid grid-cols-3 bg-zinc-50 font-bold text-zinc-500 uppercase tracking-wider p-2">
                    <span>Quantity</span><span>Total</span><span className="text-right">Unit</span>
                  </div>
                  {p.quantity_tiers.map((t, i) => {
                    const total = t.quantity * t.unit_price;
                    const firstUnit = p.quantity_tiers[0].unit_price;
                    const savings = firstUnit > 0 ? Math.round(((firstUnit - t.unit_price) / firstUnit) * 100) : 0;
                    const active = qty >= t.quantity && (i === p.quantity_tiers.length - 1 || qty < p.quantity_tiers[i + 1].quantity);
                    return (
                      <div
                        key={t.quantity}
                        className={`grid grid-cols-3 p-2 border-t border-zinc-100 cursor-pointer hover:bg-orange-50 ${active ? "bg-orange-50/60" : ""}`}
                        onClick={() => setQty(t.quantity)}
                        data-testid={`tier-row-${t.quantity}`}
                      >
                        <span className="font-semibold">Upto {t.quantity}</span>
                        <span className="font-mono">₹{Math.round(total).toLocaleString("en-IN")}{savings > 0 && <span className="text-emerald-600 text-[10px] ml-1">Save {savings}%</span>}</span>
                        <span className="text-right font-mono">₹{t.unit_price}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Estimate box */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 flex justify-between items-center">
                <div>
                  <div className="text-xs uppercase font-bold text-zinc-500">Estimated Total</div>
                  <div className="text-3xl font-mono font-black text-[#FF6B00]" data-testid="price-total">
                    ₹{price ? Math.round(price.line_total).toLocaleString("en-IN") : "..."}
                  </div>
                  {price && (
                    <div className="text-[10px] text-zinc-600 mt-1">
                      ₹{price.unit_price} / unit · {qty} units · <span className="font-bold text-emerald-700">incl. of {p.gst_rate ?? 18}% GST</span>
                    </div>
                  )}
                  {price && price.design_fee > 0 && (
                    <div className="mt-1 text-[11px] font-bold text-emerald-700" data-testid="design-fee-line">
                      + ₹{Math.round(price.design_fee).toLocaleString("en-IN")} design service{(p.design_pages || 1) > 1 ? ` (${p.design_pages} pages)` : ""}
                    </div>
                  )}
                </div>
                <Sparkles className="text-[#FF6B00]" size={40} />
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAdd}
                  disabled={adding}
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#FF6B00] text-[#FF6B00] font-bold px-6 py-3 rounded-full hover:bg-[#FF6B00] hover:text-white transition-colors disabled:opacity-60"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart size={16} /> {adding ? "Adding…" : "Add to Cart"}
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={adding}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white font-bold px-6 py-3 rounded-full hover:bg-[#e65a00] transition-colors disabled:opacity-60"
                  data-testid="buy-now-btn"
                >
                  Get Started <Sparkles size={16} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-zinc-600 pt-2">
                <div className="flex items-center gap-1.5"><Truck size={12} className="text-emerald-600" /> FREE shipping over ₹1,000</div>
                <div className="flex items-center gap-1.5"><Check size={12} className="text-emerald-600" /> Bulk pricing auto-applied</div>
              </div>
            </div>
          )}

          {/* Design Decision — three clear options */}
          <div className="mt-6 border-2 border-zinc-200 rounded-2xl p-5" data-testid="design-choice-section">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 size={18} className="text-[#FF6B00]" />
              <div className="font-display text-lg font-black tracking-tight">Design your {p.name}</div>
            </div>
            <div className="text-xs text-zinc-500 mb-4">Pick one — your choice moves with this item to checkout & production.</div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(() => {
                const dfPages = p.design_pages || 1;
                const dfFee = Math.round((p.design_charge && p.design_charge > 0) ? p.design_charge : 499 * dfPages);
                return [
                  { v: "upload_now", icon: Upload, title: "Upload now", desc: "Attach your print-ready file here", tid: "design-upload-now" },
                  { v: "upload_later", icon: Clock, title: "Upload later", desc: "Pay now, send file within 7 days", tid: "design-upload-later" },
                  { v: "design_for_me", icon: Palette, title: "Design for me", desc: `Our team designs it — ₹${dfFee.toLocaleString("en-IN")}${dfPages > 1 ? ` (${dfPages} pages)` : ""}`, tid: "design-for-me" },
                ];
              })().map((o) => {
                const Icon = o.icon;
                const sel = designChoice === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setDesignChoice(o.v)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${sel ? "border-[#FF6B00] bg-orange-50" : "border-zinc-200 hover:border-zinc-400"}`}
                    data-testid={o.tid}
                  >
                    <Icon size={20} className={sel ? "text-[#FF6B00]" : "text-zinc-500"} />
                    <div className="mt-2 font-bold text-sm">{o.title}</div>
                    <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{o.desc}</div>
                    {sel && <div className="mt-2 text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider inline-flex items-center gap-1"><Check size={10} /> Selected</div>}
                  </button>
                );
              })}
            </div>

            {designChoice === "upload_now" && (
              <div className="mt-4 border-2 border-dashed border-zinc-300 rounded-xl p-4" data-testid="design-upload-panel">
                <label className="flex flex-col items-center justify-center py-4 cursor-pointer hover:bg-orange-50 rounded-lg transition-colors">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.ai,.cdr,.eps,.psd,.svg,.tiff,.tif,.webp"
                    onChange={handleDesignFileUpload}
                    disabled={uploadingDesign}
                    className="hidden"
                    data-testid="design-file-input"
                  />
                  <Upload size={24} className="text-[#FF6B00] mb-2" />
                  <div className="font-bold text-sm">{uploadingDesign ? "Uploading…" : "Click to upload your design"}</div>
                  <div className="text-xs text-zinc-500 mt-1">PDF, AI, CDR, EPS, PSD, JPG, PNG · up to 50 MB</div>
                </label>
                {designFiles.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="design-files-list">
                    {designFiles.map((f) => (
                      <div key={f.media_id} className="flex items-center justify-between bg-white border border-zinc-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={14} className="text-emerald-600 flex-shrink-0" />
                          <div className="text-xs truncate">
                            <div className="font-bold truncate">{f.filename}</div>
                            <div className="text-zinc-500">{(f.size / 1024).toFixed(0)} KB · uploaded</div>
                          </div>
                        </div>
                        <button type="button" onClick={() => removeDesignFile(f.media_id)}
                          className="p-1 text-zinc-400 hover:text-red-500"
                          data-testid={`remove-design-${f.media_id}`}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {designChoice === "upload_later" && (
              <div className="mt-4 text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="design-later-info">
                💡 We'll email you a secure upload link right after payment. You have 7 days to send us the file — production starts as soon as we receive it.
              </div>
            )}

            {designChoice === "design_for_me" && (
              <div className="mt-4 text-xs text-zinc-600 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="design-for-me-info">
                🎨 Our in-house design team will craft this for you. Add a brief below so they nail your vibe.
                <textarea
                  value={designNotes}
                  onChange={(e) => setDesignNotes(e.target.value)}
                  rows={3}
                  placeholder="Tell us about your brand, colours, style preferences, text/logo you'd like included…"
                  className="mt-2 w-full border-2 border-emerald-200 focus:border-emerald-500 rounded-lg px-3 py-2 text-sm bg-white outline-none"
                  data-testid="design-brief-input"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Description */}
      {p.description && (
        <section className="max-w-4xl mx-auto px-4 lg:px-8 py-10 border-t border-zinc-100">
          <div className="overline mb-2 text-[#FF6B00]">About this product</div>
          <p className="text-zinc-700 leading-relaxed text-lg">{p.description}</p>
        </section>
      )}

      {/* FAQ */}
      {(p.faqs || []).length > 0 && (
        <section className="max-w-4xl mx-auto px-4 lg:px-8 py-8 border-t border-zinc-100">
          <div className="overline mb-2">Help</div>
          <h2 className="font-display text-3xl font-black tracking-tighter mb-6">Frequently Asked Questions</h2>
          <div className="space-y-2" data-testid="product-faqs">
            {p.faqs.map((f, i) => (
              <div key={i} className="border border-zinc-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex justify-between items-center text-left p-4 hover:bg-zinc-50 font-semibold"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span>{f.q}</span>
                  {openFaq === i ? <ChevronUp size={18} className="text-[#FF6B00]" /> : <ChevronDown size={18} className="text-zinc-400" />}
                </button>
                {openFaq === i && <div className="px-4 pb-4 text-zinc-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-12 border-t border-zinc-100">
          <div className="overline mb-2">More from {p.group}</div>
          <h2 className="font-display text-3xl font-black tracking-tighter mb-6">Related Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((r) => {
              const a = GROUP_ACCENTS[r.group] || "from-zinc-500 to-zinc-700";
              return (
                <Link key={r.id} to={`/products/${r.id}`} className="group rounded-2xl overflow-hidden border-2 border-zinc-200 hover:border-[#FF6B00] transition-all bg-white block" data-testid={`related-${r.id}`}>
                  <div className={`aspect-square bg-gradient-to-br ${a} flex items-center justify-center p-4`}>
                    <div className="text-white font-display text-lg font-black text-center leading-tight">{r.name}</div>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-sm truncate">{r.name}</div>
                    <div className="font-mono text-[#FF6B00] font-black text-sm">Starts ₹{Math.round((r.quantity_tiers?.[0]?.unit_price ?? r.base_price) * (r.min_quantity || 1)).toLocaleString("en-IN")}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function OptionRow({ option, value, onChange, designPages = 1 }) {
  const type = option.type || "select";
  const label = option.label || option.key;

  // Compute effective ₹ delta for a value (multiplies per_page charges by designPages).
  const effDelta = (v) => {
    const base = Number(v.price_delta || 0);
    return v.per_page ? base * Math.max(1, designPages) : base;
  };
  const priceSuffix = (v) => {
    const d = effDelta(v);
    if (!d) return "";
    const sign = d > 0 ? "+" : "";
    if (v.per_page && designPages > 1) {
      return ` (${sign}₹${Number(v.price_delta)} × ${designPages} pages = ${sign}₹${d})`;
    }
    return ` (${sign}₹${d})`;
  };

  if (type === "select") {
    return (
      <div>
        <label className="block text-xs uppercase tracking-wider font-bold text-zinc-600 mb-1">{label}</label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="input w-full"
          data-testid={`option-${option.key}`}
        >
          {(option.values || []).map((v) => (
            <option key={v.value} value={v.value}>{v.label}{priceSuffix(v)}</option>
          ))}
        </select>
      </div>
    );
  }

  // swatch or radio
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-600 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2" data-testid={`option-${option.key}`}>
        {(option.values || []).map((v) => {
          const sel = value === v.value;
          const d = effDelta(v);
          return (
            <button
              key={v.value}
              type="button"
              onClick={() => onChange(v.value)}
              className={`text-xs font-semibold px-3 py-2 rounded-lg border-2 transition-colors ${sel ? "border-[#FF6B00] bg-orange-50 text-[#FF6B00]" : "border-zinc-200 hover:border-zinc-400 text-zinc-700"}`}
              data-testid={`option-${option.key}-${v.value}`}
            >
              {v.label}{d ? <span className="ml-1 text-[10px] font-mono text-zinc-500">{d > 0 ? "+" : ""}₹{d}</span> : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}
